// src/pages/UserManagement.js
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import {
  getUserRoles,
  assignRoleToUser,
  revokeRoleFromUser,
  listRoles,
  listInvitations,
  listOrganizationUsers,
  deleteUser,
} from "../Api";
import { getOrganizationId, getUserEmail } from "../utils/authUtils";
import { FaTimes, FaUserCircle } from "react-icons/fa";
import "./UserManagement.css";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedRoleToAssign, setSelectedRoleToAssign] = useState("");
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    try {
      const data = await listRoles();
      setRoles(data || []);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      toast.error("Failed to load roles");
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const orgId = getOrganizationId();
      if (!orgId) {
        toast.error("Organization ID not found");
        return;
      }

      // Fetch all users in the organization using the new backend endpoint
      const usersData = await listOrganizationUsers(orgId);

      // The backend already includes roles and role_names in the response
      // Map the response to match the component's expected format
      const usersWithRoles = (usersData || []).map(user => ({
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        created_at: user.created_at || '',
        last_login: user.last_login,
        is_active: user.is_active,
        organization_id: user.organization_id,
        // Map backend response to component format
        user_roles: (user.roles || []).map((roleId, index) => ({
          role_id: roleId,
          role_name: user.role_names?.[index] || roleId
        }))
      }));

      console.log(`[UserManagement] Loaded ${usersWithRoles.length} users from organization ${orgId}`);
      setUsers(usersWithRoles);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageRoles = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    setSelectedRoleToAssign("");
  };

  const handleAssignRole = async () => {
    if (!selectedRoleToAssign) {
      toast.error("Please select a role");
      return;
    }

    setIsAssigning(true);
    try {
      const orgId = getOrganizationId();
      await assignRoleToUser({
        user_email: selectedUser.email,
        role_id: selectedRoleToAssign,
        organization_id: orgId,
      });

      toast.success("Role assigned successfully");

      // Refresh user roles
      const updatedRoles = await getUserRoles(selectedUser.email);
      const updatedUser = { ...selectedUser, user_roles: updatedRoles };
      setSelectedUser(updatedUser);

      // Update users list
      setUsers((prev) =>
        prev.map((u) => (u.email === selectedUser.email ? updatedUser : u))
      );

      setSelectedRoleToAssign("");
    } catch (error) {
      console.error("Failed to assign role:", error);
      toast.error("Failed to assign role");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRevokeRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to revoke this role?")) {
      return;
    }

    try {
      const orgId = getOrganizationId();
      await revokeRoleFromUser(selectedUser.email, roleId, orgId);

      toast.success("Role revoked successfully");

      // Refresh user roles
      const updatedRoles = await getUserRoles(selectedUser.email);
      const updatedUser = { ...selectedUser, user_roles: updatedRoles };
      setSelectedUser(updatedUser);

      // Update users list
      setUsers((prev) =>
        prev.map((u) => (u.email === selectedUser.email ? updatedUser : u))
      );
    } catch (error) {
      console.error("Failed to revoke role:", error);
      toast.error("Failed to revoke role");
    }
  };

  const canDeleteUser = (user) => {
    const currentUserEmail = getUserEmail();

    // Prevent self-deletion
    if (user.email === currentUserEmail) {
      return { canDelete: false, reason: "Cannot delete your own account" };
    }

    // Check if user has only Viewer or Manager roles
    const hasAdminRole = user.user_roles.some((ur) => {
      const roleName = getRoleName(ur.role_id).toLowerCase();
      return roleName.includes("admin");
    });

    if (hasAdminRole) {
      return { canDelete: false, reason: "Cannot delete Admin users" };
    }

    return { canDelete: true, reason: null };
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const orgId = getOrganizationId();
      await deleteUser(userToDelete.email, orgId);

      toast.success(`User ${userToDelete.email} deleted successfully`);

      // Remove from local state
      setUsers((prev) => prev.filter((u) => u.email !== userToDelete.email));

      // Close modals
      setShowDeleteModal(false);
      setShowUserModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error(error.message || "Failed to delete user");
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.role_id === roleId);
    return role ? role.name : roleId;
  };

  const getRoleBadgeClass = (roleName) => {
    const name = roleName.toLowerCase();
    if (name.includes("admin")) return "role-badge role-admin";
    if (name.includes("manager")) return "role-badge role-manager";
    if (name.includes("viewer")) return "role-badge role-viewer";
    return "role-badge";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesRole =
      !roleFilter ||
      user.user_roles.some((ur) => ur.role_id === roleFilter);

    return matchesSearch && matchesRole;
  });

  const getAvailableRoles = () => {
    if (!selectedUser) return roles;
    const userRoleIds = selectedUser.user_roles.map((ur) => ur.role_id);
    return roles.filter((role) => !userRoleIds.includes(role.role_id));
  };

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-section">
        <div className="user-management-container">
          <h1 className="page-header">User Management</h1>

          {/* Filters Card */}
          <div className="user-card">
            <div className="filters-section">
              <div className="filter-group">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <select
                  className="filter-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All Roles</option>
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Users Table Card */}
          <div className="user-card">
            <div className="card-header">
              <h2>Organization Members</h2>
              <span className="user-count">{filteredUsers.length} users</span>
            </div>
            <div className="card-body">
              {isLoading ? (
                <div className="loading-text">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="empty-state">
                  {searchTerm || roleFilter
                    ? "No users found matching your filters"
                    : "No users found"}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.email}>
                          <td>
                            <div className="user-info">
                              <FaUserCircle className="user-avatar-icon" />
                              <span>
                                {user.first_name} {user.last_name}
                              </span>
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <div className="roles-cell">
                              {user.user_roles.length === 0 ? (
                                <span className="no-roles">No roles</span>
                              ) : (
                                user.user_roles.map((ur) => (
                                  <span
                                    key={ur.role_id}
                                    className={getRoleBadgeClass(
                                      getRoleName(ur.role_id)
                                    )}
                                  >
                                    {getRoleName(ur.role_id)}
                                  </span>
                                ))
                              )}
                            </div>
                          </td>
                          <td>{formatDate(user.created_at)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-manage"
                                onClick={() => handleManageRoles(user)}
                              >
                                Manage Roles
                              </button>
                              {canDeleteUser(user).canDelete ? (
                                <button
                                  className="btn-delete"
                                  onClick={() => {
                                    setUserToDelete(user);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Delete user"
                                >
                                  Delete
                                </button>
                              ) : (
                                <span
                                  className="delete-disabled"
                                  title={canDeleteUser(user).reason}
                                >
                                  Delete
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* User Modal */}
        {showUserModal && selectedUser && (
          <div
            className="modal-overlay"
            onClick={() => setShowUserModal(false)}
          >
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>Manage User Roles</h2>
                  <p className="modal-subtitle">{selectedUser.email}</p>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setShowUserModal(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="modal-body">
                {/* Current Roles */}
                <div className="modal-section">
                  <h3 className="modal-section-title">Current Roles</h3>
                  {selectedUser.user_roles.length === 0 ? (
                    <div className="empty-roles">
                      This user has no roles assigned
                    </div>
                  ) : (
                    <div className="current-roles-list">
                      {selectedUser.user_roles.map((ur) => (
                        <div key={ur.role_id} className="role-item">
                          <span className={getRoleBadgeClass(getRoleName(ur.role_id))}>
                            {getRoleName(ur.role_id)}
                          </span>
                          <button
                            className="btn-remove-role"
                            onClick={() => handleRevokeRole(ur.role_id)}
                            title="Remove role"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assign New Role */}
                <div className="modal-section">
                  <h3 className="modal-section-title">Assign New Role</h3>
                  <div className="assign-role-form">
                    <select
                      className="form-select"
                      value={selectedRoleToAssign}
                      onChange={(e) => setSelectedRoleToAssign(e.target.value)}
                      disabled={isAssigning}
                    >
                      <option value="">Select a role to assign</option>
                      {getAvailableRoles().map((role) => (
                        <option key={role.role_id} value={role.role_id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn-assign"
                      onClick={handleAssignRole}
                      disabled={!selectedRoleToAssign || isAssigning}
                    >
                      {isAssigning ? "Assigning..." : "Assign Role"}
                    </button>
                  </div>
                </div>

                {/* User Info */}
                <div className="modal-section">
                  <h3 className="modal-section-title">User Information</h3>
                  <div className="user-info-grid">
                    <div className="info-row">
                      <span className="info-label">Name:</span>
                      <span className="info-value">
                        {selectedUser.first_name} {selectedUser.last_name}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{selectedUser.email}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Joined:</span>
                      <span className="info-value">
                        {formatDate(selectedUser.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              className="modal-content modal-small"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Delete User</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowDeleteModal(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="modal-body">
                <div className="warning-message">
                  <strong>Warning:</strong> This action cannot be undone!
                </div>
                <p>Are you sure you want to delete this user?</p>
                <div className="user-details">
                  <p>
                    <strong>Name:</strong> {userToDelete.first_name}{" "}
                    {userToDelete.last_name}
                  </p>
                  <p>
                    <strong>Email:</strong> {userToDelete.email}
                  </p>
                  <p>
                    <strong>Roles:</strong>{" "}
                    {userToDelete.user_roles
                      .map((ur) => getRoleName(ur.role_id))
                      .join(", ")}
                  </p>
                </div>
                <p className="delete-info">This will remove:</p>
                <ul className="delete-list">
                  <li>User account from the organization</li>
                  <li>All role assignments</li>
                  <li>All camera permissions</li>
                </ul>
                <p className="audit-info">
                  Audit logs and historical events will be preserved.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button className="btn-danger" onClick={handleDeleteUser}>
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
